/**
 * AtlasMusicModule — Minecraft-style background music (Sonilo tracks under
 * /music/<key>.mp3, see tools/asset-pipeline/gen-music.mjs).
 *
 * The Minecraft doctrine, deliberately: music is OCCASIONAL. Long random
 * silences (3–7 min) separate tracks; when the gap expires a track is
 * drawn at random from the current CONTEXT BUCKET (menu / space /
 * settlement / night / ground), plays ONCE to completion at low gain, and
 * the silence returns. No looping, no constant soundtrack — the rarity is
 * what gives a track its moment. A context change mid-track lets the track
 * finish (also the Minecraft behavior).
 *
 * The one interruption: COMBAT. When space combat flares (atlas.combatSpace,
 * published by SpaceModule) or a wilds creature presses the player, the
 * current track fades fast and the combat track LOOPS until the danger has
 * been clear for a grace period — then silence, and the ordinary cycle
 * resumes with a short gap.
 *
 * Since 2026-09-20: a client of the shared audio runtime (no AudioContext, no
 * cache of its own — the track rides the `music` bus, so it has a volume, it
 * ducks under speech, and a decoded track is RELEASED when the store needs the
 * room: twelve tracks at ~19 MB of PCM each used to stay for the session). And
 * "danger" is no longer two things: PvP, a committed bounty hunter and a battle
 * you stand in report through lib/audio/threat.ts. The doctrine is untouched —
 * the gaps, the silence, play-once, the 8 s clear.
 */
import type { App, IModule } from 'enginex';
import { audioRuntime } from '../lib/audio/runtime.js';
import { activeThreats } from '../lib/audio/threat.js';
import { myContext, myEnrollment } from '../lib/pvp_context.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';

const MUSIC_GAIN = 0.22;
const COMBAT_GAIN = 0.3;
const FADE_IN_S = 3;
const FADE_OUT_S = 2;
const COMBAT_FADE_S = 0.8;
/** Random silence between tracks (Minecraft: minutes of quiet). */
const GAP_MIN_S = 180;
const GAP_MAX_S = 420;
/** First gap after boot — shorter, so a session hears music soonish. */
const FIRST_GAP_MIN_S = 60;
const FIRST_GAP_MAX_S = 150;
/** Gap after combat resolves. */
const POST_COMBAT_GAP_MIN_S = 45;
const POST_COMBAT_GAP_MAX_S = 90;
/** Combat must stay clear this long before the loop stands down. */
const COMBAT_CLEAR_S = 8;
/** A creature pressing within this range is ground combat. */
const GROUND_COMBAT_RANGE_M = 60;
const TIER_RADIUS: Record<string, number> = { city: 420, town: 280, hamlet: 170, capital: 3230, district: 420, circuit: 280 }; // capital wave

const BUCKETS: Record<string, string[]> = {
  menu: ['music_menu'],
  space: ['music_space_1', 'music_space_2'],
  settlement: ['music_town_1', 'music_town_2'],
  night: ['music_night', 'music_wander_4'],
  ground: ['music_wander_1', 'music_wander_2', 'music_wander_3', 'music_wander_4'],
};
const COMBAT_TRACKS = { space: 'music_combat_space', ground: 'music_combat_ground' };

/** 'loading' = the boot loading screen's own track (the space-combat piece,
 *  looped from the loading clock's start until atlas:loading-done). */
type MusicPhase = 'gap' | 'playing' | 'combat' | 'loading';
/** The loading screen's track (2026-09-10, "play the getting attacked in
 *  space music till the loading is done"). */
const LOADING_TRACK = 'music_combat_space';
const LOADING_GAIN = 0.3;

export class AtlasMusicModule implements IModule {
  readonly name = 'AtlasMusic';

  private phase: MusicPhase = 'gap';
  private gapRemaining = FIRST_GAP_MIN_S + Math.random() * (FIRST_GAP_MAX_S - FIRST_GAP_MIN_S);
  private current: { key: string; source: AudioBufferSourceNode; gain: GainNode; unpin: () => void } | null = null;
  /** Why the score is in combat (probe surface): '' | space | creature | pvp | hunter | battle | airwar. */
  private combatWhy = '';
  private lastTrack = '';
  private combatClearT = 0;
  private sites: SettlementSite[] = [];
  private sitesSeed = '';
  private acc = 0;
  /** The loading screen wants its track (armed on menu-enter, cleared on loading-done). */
  private loadingWanted = false;

  init(app: App): void {
    // Decode the loading track the moment the shared context exists — the screen may open seconds later.
    audioRuntime().onChange(() => { if (audioRuntime().ctx) this.buffer(LOADING_TRACK); });
    // THE LOADING SCREEN'S TRACK (2026-09-10): from the moment the loading
    // clock starts (menu-enter — forwarded after the intro, which held the
    // room muted) until atlas:loading-done, the space-combat piece LOOPS
    // over the paintings; then it fades and the ordinary cycle resumes after
    // a first-gap silence. A reconnect resume also emits menu-enter — with
    // no gesture yet there is no AudioContext, and the machine simply waits.
    bus.on('atlas:menu-enter', () => { this.loadingWanted = true; this.startLoading(); });
    bus.on('atlas:loading-done', () => {
      this.loadingWanted = false;
      if (this.phase === 'loading') {
        this.stopCurrent(FADE_OUT_S);
        this.phase = 'gap';
        this.gapRemaining = FIRST_GAP_MIN_S + Math.random() * (FIRST_GAP_MAX_S - FIRST_GAP_MIN_S);
      }
    });

    app.world.addSystem(makeSystem('AtlasMusic', Phase.UI, 62, (dt) => {
      this.acc += dt;
      if (this.acc < 1) return;
      const step = this.acc;
      this.acc = 0;
      this.tick(step);
    }));

    // Probe surface + a gap-skip lever (headless can't wait minutes).
    (globalThis as unknown as { __music?: () => object }).__music = () => ({
      phase: this.phase,
      bucket: this.bucket(),
      track: this.current?.key ?? null,
      loadingWanted: this.loadingWanted,
      gapRemaining: Math.round(this.gapRemaining),
      combat: this.combatNow(),
      combatWhy: this.combatWhy,
      ctx: audioRuntime().ctx?.state ?? 'uninitialised',
    });
    (globalThis as unknown as { __musicSkipGap?: () => void }).__musicSkipGap = () => { this.gapRemaining = 0; };
  }

  // ── The Minecraft machine ─────────────────────────────────────────────────

  private tick(dt: number): void {
    // The loading screen owns the music while it is up (its buffer may land
    // a second or two after the clock starts — keep trying until it plays).
    if (this.loadingWanted) { if (this.phase !== 'loading') this.startLoading(); return; }
    if (this.phase === 'loading') { this.stopCurrent(FADE_OUT_S); this.phase = 'gap'; }
    const combat = this.combatNow();

    if (this.phase === 'combat') {
      if (combat) {
        this.combatClearT = 0;
      } else {
        this.combatClearT += dt;
        if (this.combatClearT >= COMBAT_CLEAR_S) {
          this.stopCurrent(FADE_OUT_S);
          this.phase = 'gap';
          this.gapRemaining = POST_COMBAT_GAP_MIN_S + Math.random() * (POST_COMBAT_GAP_MAX_S - POST_COMBAT_GAP_MIN_S);
        }
      }
      return;
    }

    if (combat) {
      // Danger interrupts anything — the one exception to play-to-completion.
      this.stopCurrent(COMBAT_FADE_S);
      const key = atlas.spaceFlight ? COMBAT_TRACKS.space : COMBAT_TRACKS.ground;
      const started = this.play(key, COMBAT_GAIN, true);
      this.phase = started ? 'combat' : 'gap';
      this.combatClearT = 0;
      if (!started) this.gapRemaining = 20; // clip missing — retry the pick later
      return;
    }

    if (this.phase === 'playing') return; // let the track finish (onended re-arms the gap)

    this.gapRemaining -= dt;
    if (this.gapRemaining > 0) return;
    const pool = BUCKETS[this.bucket()] ?? BUCKETS.ground;
    const pick = this.pickTrack(pool);
    if (this.play(pick, MUSIC_GAIN, false)) {
      this.phase = 'playing';
      this.lastTrack = pick;
    } else {
      this.gapRemaining = 20; // buffer still loading / missing — try again soon
    }
  }

  /** Context bucket — menu, space, settlement (tier radius), night, ground. */
  private bucket(): string {
    if (atlas.currentWorldId === null) return 'menu';
    if (atlas.spaceFlight) return 'space';
    const conn = atlas.conn;
    if (conn && atlas.currentWorldId === atlas.commonsWorldId) {
      let seed = '';
      for (const w of conn.db.world.iter()) if (w.worldId === atlas.currentWorldId) { seed = w.terrainSeed; break; }
      if (seed !== '') {
        if (this.sitesSeed !== seed) {
          this.sites = settlementSites(seed);
          this.sitesSeed = seed;
        }
        for (const s of this.sites) {
          if (Math.hypot(s.centerX - atlas.localX, s.centerZ - atlas.localZ) < (TIER_RADIUS[s.tier] ?? 0)) return 'settlement';
        }
      }
      // Night on the commons: the replicated sky clock.
      try {
        const sky = conn.db.skyState.worldId.find(atlas.currentWorldId);
        if (sky && (sky.timeOfDay < 0.23 || sky.timeOfDay > 0.77)) return 'night';
      } catch { /* sky not replicated yet */ }
    }
    return 'ground';
  }

  /** Space combat is published by SpaceModule; ground combat = a creature
   *  actively targeting this player nearby (replicated creature rows). */
  private combatNow(): boolean {
    this.combatWhy = '';
    if (atlas.combatSpace) { this.combatWhy = 'space'; return true; }
    const conn = atlas.conn;
    if (!conn || !conn.identity || atlas.currentWorldId === null) return false;
    // Dangers other systems REPORT (a lease that lapses on its own): a committed hunter, a battle you stand in, the air war.
    const reported = activeThreats(performance.now());
    if (reported.length > 0) { this.combatWhy = reported[0]; return true; }
    if (atlas.spaceFlight) return false;
    // PvP, read straight off the replicated rows: a live duel, a live arena round, or a sector fight you are LOCKED into
    // (merely standing in a PvP sector is not combat).
    try {
      const ctx = myContext();
      if (ctx === 'duel' || ctx === 'arena' || (ctx === 'field' && myEnrollment()?.state === 'engaged')) { this.combatWhy = 'pvp'; return true; }
    } catch { /* pvp tables not subscribed in this world */ }
    try {
      for (const c of conn.db.creature.iter()) {
        if (c.state !== 'aggro' || c.targetKind !== 'player') continue;
        if (!c.targetPlayer.isEqual(conn.identity)) continue;
        if (Math.hypot(c.x - atlas.localX, c.z - atlas.localZ) < GROUND_COMBAT_RANGE_M) { this.combatWhy = 'creature'; return true; }
      }
    } catch { /* creatures not subscribed in this world */ }
    return false;
  }

  /** Start the loading track if the context and its buffer are ready. */
  private startLoading(): void {
    if (this.phase === 'loading' && this.current?.key === LOADING_TRACK) return;
    if (!audioRuntime().ctx) return; // no gesture yet (a resume reload) — nothing can play
    if (this.current && this.current.key !== LOADING_TRACK) this.stopCurrent(COMBAT_FADE_S);
    if (this.play(LOADING_TRACK, LOADING_GAIN, true)) this.phase = 'loading';
  }

  private pickTrack(pool: string[]): string {
    if (pool.length === 1) return pool[0];
    const options = pool.filter((k) => k !== this.lastTrack);
    return options[Math.floor(Math.random() * options.length)];
  }

  // ── WebAudio plumbing ─────────────────────────────────────────────────────

  private play(key: string, gain: number, loop: boolean): boolean {
    const buf = this.buffer(key);
    const rt = audioRuntime();
    const ctx = rt.ctx, out = rt.busInput('music');
    if (!buf || !ctx || !out) return false;
    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.loop = loop;
    const g = ctx.createGain();
    g.gain.value = 0;
    source.connect(g);
    g.connect(out);
    source.start();
    g.gain.setValueAtTime(0, ctx.currentTime);
    g.gain.linearRampToValueAtTime(gain, ctx.currentTime + FADE_IN_S);
    const layer = { key, source, gain: g, unpin: rt.store.pin(`m:${key}`) }; // the playing track is never evicted
    this.current = layer;
    if (!loop) {
      source.onended = () => {
        if (this.current === layer) {
          this.current = null;
          this.phase = 'gap';
          this.gapRemaining = GAP_MIN_S + Math.random() * (GAP_MAX_S - GAP_MIN_S);
        }
        layer.unpin();
        try { source.disconnect(); g.disconnect(); } catch { /* fine */ }
      };
    }
    return true;
  }

  private stopCurrent(fadeS: number): void {
    const cur = this.current;
    const ctx = audioRuntime().ctx;
    if (!cur || !ctx) { this.current = null; return; }
    this.current = null;
    cur.source.onended = null;
    cur.gain.gain.cancelScheduledValues(ctx.currentTime);
    cur.gain.gain.setValueAtTime(cur.gain.gain.value, ctx.currentTime);
    cur.gain.gain.linearRampToValueAtTime(0, ctx.currentTime + fadeS);
    // The timeout owns the OLD layer by closure — a track started meanwhile can never be cut by it.
    window.setTimeout(() => {
      cur.unpin();
      try { cur.source.stop(); cur.source.disconnect(); cur.gain.disconnect(); } catch { /* fine */ }
    }, fadeS * 1000 + 100);
  }

  /** The decoded track if the shared store has it NOW; otherwise the load starts (a transient failure is retried). */
  private buffer(key: string): AudioBuffer | null {
    const rt = audioRuntime();
    return rt.ctx ? rt.store.get(`m:${key}`) : null;
  }

  dispose(): void {
    this.stopCurrent(0.1);
    // (The context is the shared runtime's — a module never closes it.)
  }
}
